import { useState, useCallback, useEffect } from "react";
import { Search, MoreVertical, Trash2 } from "lucide-react";
import { useNotifications } from "../../contexts/NotificationContext";
import { Template } from "../../types";
import { useTemplates } from "../../hooks/useTemplates";

interface TemplatesTabProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

function TemplatesTab({ searchTerm, setSearchTerm }: TemplatesTabProps) {
  const [showTemplateMenu, setShowTemplateMenu] = useState<string | null>(null);
  const { showNotification } = useNotifications();

  const { templates, isLoading: isLoadingTemplates, deleteTemplate, loadTemplates } = useTemplates();

  useEffect(() => {
    loadTemplates(searchTerm);
  }, [loadTemplates, searchTerm]);

  const handleTemplateAction = useCallback(
    async (action: string, template: Template) => {
      try {
        switch (action) {
          case "delete":
            if (confirm("Are you sure you want to delete this template?")) {
              await deleteTemplate(template.id);
              showNotification("success", "Template deleted successfully");
            }
            break;
        }
      } catch (error) {
        showNotification("error", `Failed to ${action} template`);
      }
      setShowTemplateMenu(null);
    },
    [deleteTemplate, showNotification]
  );

  // Filter templates based on search term
  const filteredTemplates = templates.filter(template => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      template.name.toLowerCase().includes(searchLower) ||
      (template.description && template.description.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className="templates-tab">
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Filter by template name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-gray-200 dark:placeholder-gray-400"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400 dark:text-gray-500" />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Template Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Description
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {isLoadingTemplates ? (
              <tr>
                <td
                  colSpan={3}
                  className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  Loading templates...
                </td>
              </tr>
            ) : (
              filteredTemplates.map((template) => (
                <tr key={template.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {template.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {template.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="relative">
                      <button
                        onClick={() => setShowTemplateMenu(template.id)}
                        className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                      >
                        <MoreVertical className="h-5 w-5" />
                      </button>
                      {/* Add overlay for click-outside */}
                      {showTemplateMenu === template.id && (
                        <div
                          className="fixed inset-0 bg-black/0 z-40"
                          onClick={() => setShowTemplateMenu(null)}
                        />
                      )}
                      {showTemplateMenu === template.id && (
                        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 dark:ring-gray-700 z-50">
                          <div className="py-1" role="menu">
                            <button
                              onClick={() =>
                                handleTemplateAction("delete", template)
                              }
                              className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TemplatesTab;
